/**
 * Frontend mirror of the backend permission catalogue.
 *
 * Single source of truth for permission keys + a `hasPermission` helper
 * that mirrors the backend logic. Used by the nav, route gates, and
 * any in-page capability checks.
 *
 * Keep in sync with `app/services/permissions.py` on the backend.
 */

export const PERM = {
  KPIS_ALL: "kpis.all",
  KPIS_SCOPED: "kpis.scoped",
  MP_ALL: "mass_payments.all",
  MP_PUSH_TO_SF: "mass_payments.push_to_sf",
  MP_API_PAY: "mass_payments.api_pay",
  /** Outbound payments over the iFAST API (FPS, SEPA, SWIFT). */
  SETTLEMENTS_API_RELEASE: "settlements.api_release",
  /** Internal transfers between our iFAST accounts over the API (OWN). */
  SETTLEMENTS_API_TRANSFER: "settlements.api_transfer",
  TRADES_IFAST_EXECUTE: "trades.ifast_execute",
  REPORTING: "mfx.reporting",
  ENTITY_SCREENING: "mfx.entity_screening",
  SAFEGUARDING: "mfx.safeguarding",
  COMMISSIONS: "mfx.commissions",
  INVITE_CLIENT: "mfx.invite_client",
  OPS_TOOLS: "mfx.operations_tools",
  COMPLIANCE_TOOLS: "mfx.compliance_tools",
  CONTROL_CENTRE: "mfx.control_centre",
  NOTIFICATIONS: "mfx.notifications",
  LEADS_ALL: "leads.all",
  LEADS_SCOPED: "leads.scoped",
  RISK: "mfx.risk",
  // Bank Payments: one permission per tab; the page shows with any of them.
  BP_QUEUE: "mfx.bank_payments.queue",
  BP_BALANCES: "mfx.bank_payments.balances",
  BP_EXCLUSIONS: "mfx.bank_payments.exclusions",
  BP_COUNTERPARTIES: "mfx.bank_payments.counterparties",
  BP_AUDIT: "mfx.bank_payments.audit",
  BP_IFAST_STATEMENTS: "mfx.bank_payments.ifast_statements",
  BP_IFAST_RECON: "mfx.bank_payments.ifast_recon",
  SETTLEMENTS_QUEUE: "mfx.settlements_queue",
  NEW_PAYMENTS: "mfx.new_payments",
  COMPLIANCE_QUEUE: "mfx.compliance_queue",
  KPIS_COMPLIANCE_ALL: "kpis.compliance_all",
  KPIS_COMPLIANCE_OWN: "kpis.compliance_own",
  COMPLIANCE_QUEUE_OFFICER: "compliance_queue.officer",
  ADJUDICATION: "compliance.adjudication",
  ADJUDICATION_CONFIG: "compliance.adjudication_config",
  /** Pay Now page: set up immediate iFAST payments. */
  PAY_NOW: "mfx.pay_now",
  /** Approve Pay Now payments (second person, code) and send them again. */
  PAY_NOW_APPROVE: "pay_now.approve",
  /** Manage Pay Now's approved beneficiaries list. */
  PAY_NOW_BENEFICIARIES: "pay_now.beneficiaries",
  /** Connect office TVs to the Display Page and manage them. */
  DISPLAY_SCREENS: "mfx.display_screens",
} as const;

export type PermKey = (typeof PERM)[keyof typeof PERM];

export const BP_TAB_PERMS: PermKey[] = [
  PERM.BP_QUEUE, PERM.BP_BALANCES, PERM.BP_EXCLUSIONS,
  PERM.BP_COUNTERPARTIES, PERM.BP_AUDIT, PERM.BP_IFAST_STATEMENTS, PERM.BP_IFAST_RECON,
];
/** The old single Bank Payments permission. Until the server splits it
 * (it does on start-up) it still opens the page. */
const BP_LEGACY = "mfx.bank_payments";

/** Match the backend bypass: mfx_admin role and is_super_admin always
 * pass every check regardless of the permissions array. */
export function hasPermission(
  user: { role?: string; isSuperAdmin?: boolean; permissions?: string[] } | null | undefined,
  key: PermKey | string,
): boolean {
  if (!user) return false;
  if (user.role === "mfx_admin") return true;
  if (user.isSuperAdmin === true) return true;
  return (user.permissions || []).includes(key);
}

export function hasAnyPermission(
  user: { role?: string; isSuperAdmin?: boolean; permissions?: string[] } | null | undefined,
  ...keys: (PermKey | string)[]
): boolean {
  return keys.some((k) => hasPermission(user, k));
}

/** Can open the Bank Payments page: any of its tabs. */
export function hasBankPaymentsAccess(
  user: { role?: string; isSuperAdmin?: boolean; permissions?: string[] } | null | undefined,
): boolean {
  if (hasAnyPermission(user, ...BP_TAB_PERMS)) return true;
  return !!user && (user.permissions || []).includes(BP_LEGACY);
}
